/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

export type AcesUrlParams = {
  partGroupId: string | undefined;
  brandId: string | undefined;
  positionId: string | undefined;
  uniqueId?: number | undefined;
};

/**
 * Builds a URL with ACES product parameters.
 * This utility is used to add product context (productPartGroupId, productBrandId, productPositionId, productUniqueId)
 * to product detail page URLs, allowing the PDP to receive these parameters
 * via router.query instead of cookies.
 *
 * @example
 * buildUrlWithAcesParams('/products/spark-plug', {
 *   partGroupId: '123',
 *   brandId: '456',
 *   positionId: '789',
 *   uniqueId: 999
 * });
 * // Returns: '/products/spark-plug?productPartGroupId=123&productBrandId=456&productPositionId=789&productUniqueId=999'
 */
export const buildUrlWithAcesParams = (baseUrl: string, params: AcesUrlParams): string => {
  if (!baseUrl) {
    return '';
  }

  const [pathname, existingSearch] = baseUrl.split('?');
  const searchParams = new URLSearchParams(existingSearch);

  if (params.partGroupId) {
    searchParams.set('productPartGroupId', params.partGroupId);
  }

  if (params.brandId) {
    searchParams.set('productBrandId', params.brandId);
  }

  if (params.positionId) {
    searchParams.set('productPositionId', params.positionId);
  }

  if (params.uniqueId) {
    searchParams.set('productUniqueId', params.uniqueId.toString());
  }

  if (params.partGroupId || params.brandId || params.positionId || params.uniqueId) {
    searchParams.set('duplicateSKURedirect', 'true');
  }

  const search = searchParams.toString();
  return search ? `${pathname}?${search}` : pathname;
};
